import { z } from 'zod';

import { FlightOptionSchema } from './flight-schemas.js';
import { ExperienceDetailsSchema } from './itinerary-item-schemas.js';
import { OccupancySchema, RoomsSchema } from './room-schemas.js';
import { ItemActorSchema } from './shared-schemas.js';

/**
 * Room shortlist metadata. A shortlist entry saves the ROOM, optionally pinned to one of its rates:
 * - No `productCode`: the card prices the room's lowest bookable rate for `occupancy`
 *   (`RoomOptionModel.leadRateFor`), so the price follows the room's live rates instead of pinning
 *   one that may sell out. This is what the app's shortlist buttons save.
 * - With `productCode` (and that rate's `occupancy`): the entry is pinned to that rate, identified
 *   by `(productCode, occupancy)` like an itinerary item. This lets shortlist groups hold the same
 *   room at different rates as separate scenarios; today only the concierge pins.
 *
 * A plain `z.object`, so unknown keys are STRIPPED rather than rejected: items saved before the
 * rate identity migration may still carry the rotating `selectedRateKey`, which is dropped.
 */
export const RoomShortlistMetadataSchema = z
  .object({
    productCode: z
      .string()
      .min(1)
      .optional()
      .describe("Pins the saved room to one of its rates: that rate's productCode, with the rate's occupancy."),
    occupancy: OccupancySchema.optional().describe(
      'Guests for ONE room — the party the saved room is priced for. Omit to use the party the room was searched for.',
    ),
    quantity: z
      .number()
      .int()
      .min(1)
      .max(10)
      .default(1)
      .describe('Number of these rooms the traveler intends to book (max 10).'),
  })
  .refine((meta) => !meta.productCode || meta.occupancy, {
    message: 'A pinned rate needs its occupancy: a rate is identified by (productCode, occupancy).',
    path: ['occupancy'],
  });

export type RoomShortlistMetadata = z.infer<typeof RoomShortlistMetadataSchema>;

/**
 * Types of items that can be shortlisted
 */
export const ShortlistItemTypeSchema = z.enum([
  'hotel',
  'room',
  'flight',
  'destination',
  'activity',
  'restaurant',
  'experience',
  'place',
]);

export type ShortlistItemType = z.infer<typeof ShortlistItemTypeSchema>;

/**
 * Reference to the original chat message where the recommendation was made
 */
export const MessageRefSchema = z.object({ chatId: z.string(), messageId: z.string() });

export type MessageRef = z.infer<typeof MessageRefSchema>;

// Re-export ShortlistItemLocation for convenience
export type { ItemLocation as ShortlistItemLocation } from './shared-schemas.js';

/**
 * Reference data schema for room shortlist items
 * Reuses RoomsSchema for the room field
 */
export const RoomReferenceDataSchema = z.object({
  room: RoomsSchema.describe('The room details'),
  hotelId: z.string().describe('The ID of the hotel in firestore'),
  hotelName: z.string().describe('The name of the hotel'),
  startDate: z.string().describe('Check-in date (ISO date string)'),
  endDate: z.string().describe('Check-out date (ISO date string)'),
});

export type RoomReferenceData = z.infer<typeof RoomReferenceDataSchema>;

/**
 * Base fields common to all shortlist items (no metadata; type-specific variants add it)
 */
const BaseShortlistItemSchema = z.object({
  title: z.string().min(1).nullish().describe('Optional display title; use displayTitle for derived title'),
  referenceId: z.string().nullish().describe('ID of the referenced entity (hotelId, destinationId, etc.)'),
  messageRef: MessageRefSchema.nullish().describe('Reference to the original chat message'),
  groupId: z.string().nullish().describe('ID of the group this item belongs to'),
  position: z.number().describe('Position of the item within its group (0-based index)'),
  metadata: z.record(z.string(), z.unknown()).optional(), // Generic for model spec; room variant uses RoomSelectionSchema
  addedBy: ItemActorSchema.optional().describe('Who added the item; absent on items saved before this was recorded'),
  updatedBy: ItemActorSchema.optional().describe('Who last changed the item; absent until its first edit'),
});

/**
 * Proper Zod discriminated union schema
 * This validates the type field and ensures referenceData (and metadata for room) match the expected schema
 */
export const ShortlistItemSchema = z.discriminatedUnion('type', [
  BaseShortlistItemSchema.extend({ type: z.literal('flight'), referenceData: FlightOptionSchema.nullish() }),
  BaseShortlistItemSchema.extend({ type: z.literal('hotel') }),
  BaseShortlistItemSchema.extend({
    type: z.literal('room'),
    referenceData: RoomReferenceDataSchema.nullish(),
    metadata: RoomShortlistMetadataSchema.describe('The party to price the room for, an optional pinned rate, and how many rooms the traveler intends to book'),
  }),
  BaseShortlistItemSchema.extend({ type: z.literal('experience'), referenceData: ExperienceDetailsSchema.optional() }),
  BaseShortlistItemSchema.extend({
    type: z.enum(['destination', 'activity', 'restaurant', 'place']),
    referenceData: z.record(z.string(), z.unknown()).optional(),
  }),
]);

/**
 * Base schema for Firestore model spec compatibility
 * This extracts the common fields without type discrimination
 */
export const ShortlistItemBaseSchema = BaseShortlistItemSchema.extend({
  type: ShortlistItemTypeSchema,
  referenceData: z.record(z.string(), z.unknown()).optional(), // Generic for model spec
});

export type ShortlistItem = z.infer<typeof ShortlistItemSchema>;

/**
 * Schema for shortlist group data
 */
export const ShortlistGroupSchema = z.object({
  name: z.string().min(1),
  position: z.number().describe('Position of the group (0-based index)'),
  source: z
    .literal('staff')
    .optional()
    .describe('Set on the one group Elara staff fill from the internal MCP; the staff tools find it by this, not by name'),
});

export type ShortlistGroup = z.infer<typeof ShortlistGroupSchema>;
